<script>
  import { replace, link, location } from 'svelte-spa-router';
  import Layout from '../components/Layout.svelte';
  import Appearance from './Appearance.svelte';
  import PaymentMethods from './PaymentMethods.svelte';
  import InvoicesImport from './InvoicesImport.svelte';
  import AnagraficaImport from './AnagraficaImport.svelte';
  import Users from './Users.svelte';
  import EmailSettings from './EmailSettings.svelte';
  import { isAdmin } from '../stores/roles.js';
  import { capabilities, canWriteInCompany } from '../stores/capabilities.js';
  import { t } from '../i18n/index.js';
  import { Palette, Banknote, Upload, FileSpreadsheet, UserCog, Mail } from '../lib/icons/index.js';

  /**
   * Settings hub — consolidates configuration-style features (payment
   * methods, bulk imports) under a single tabbed page so the sidebar stays
   * focused on day-to-day operations. Tab is encoded in the URL via the
   * second path segment so the active tab survives reloads and is
   * deep-linkable.
   *
   * We read the slug from the `$location` store rather than the router's
   * `params` prop because sibling sub-routes share this component: the
   * router never remounts Settings when moving between tabs, it only
   * swaps `params`. A store subscription re-derives the active tab on
   * every URL change without depending on how the router mutates props.
   *
   * Tabs are role-gated: a user without manage-invoices /
   * manage-customers permissions still sees the Payment Methods tab
   * (read is open to everyone), but the import tabs are hidden. If a URL
   * points to a tab the user can't access we redirect to the first
   * allowed one.
   */

  // Single source of truth for tab metadata. Order = display order.
  // `slug` matches the URL segment under /settings/.
  // `requires` is the role gate; 'none' = always shown.
  const TABS = [
    {
      slug: 'appearance',
      labelKey: 'settings.tabs.appearance',
      icon: Palette,
      component: Appearance,
      requires: 'none',
    },
    {
      slug: 'payment-methods',
      labelKey: 'settings.tabs.paymentMethods',
      icon: Banknote,
      component: PaymentMethods,
      requires: 'none',
    },
    {
      // Company admins only, per company: the global roles above cannot say
      // who administers THIS company, so the gate is the server's answer in
      // stores/capabilities.js.
      slug: 'email',
      labelKey: 'settings.tabs.email',
      icon: Mail,
      component: EmailSettings,
      requires: 'smtp',
    },
    {
      slug: 'import-invoices',
      labelKey: 'settings.tabs.importInvoices',
      icon: Upload,
      component: InvoicesImport,
      requires: 'invoices',
    },
    {
      slug: 'import-anagrafica',
      labelKey: 'settings.tabs.importAnagrafica',
      icon: FileSpreadsheet,
      component: AnagraficaImport,
      requires: 'customers',
    },
    {
      // Admin-only: the /api/user-roles/* endpoints behind this tab are
      // session-only and gated on the global `admin` role, so showing it to
      // anyone else would offer a screen that can only answer 403.
      slug: 'users',
      labelKey: 'settings.tabs.users',
      icon: UserCog,
      component: Users,
      requires: 'admin',
    },
  ];

  function isTabAllowed(tab, canInvoices, canCustomers, admin, caps) {
    if (tab.requires === 'invoices') return canInvoices;
    if (tab.requires === 'customers') return canCustomers;
    if (tab.requires === 'admin') return admin;
    if (tab.requires === 'smtp') return caps?.canManageSmtp === true;
    return true;
  }

  /** Parse the active tab slug straight from the URL: /settings/<slug>. */
  function parseTabSlug(path) {
    const match = /^\/settings\/([^/?#]+)/.exec(path || '');
    return match ? match[1] : null;
  }

  $: visibleTabs = TABS.filter(tab => isTabAllowed(tab, $canWriteInCompany, $canWriteInCompany, $isAdmin, $capabilities));
  $: urlSlug = parseTabSlug($location);
  $: activeSlug = urlSlug || visibleTabs[0]?.slug || 'payment-methods';
  $: activeTab = visibleTabs.find(tab => tab.slug === activeSlug) || visibleTabs[0];

  // Redirect when the URL points to a forbidden / unknown tab, or to bare
  // `/settings`. Reactive so it covers (a) initial mount, (b) role changes
  // at runtime (e.g. company switch revokes a permission) and (c)
  // hand-typed nonsense URLs. Guarded to only redirect when really needed,
  // otherwise we'd loop.
  //
  // `replace`, never `push`: this is a canonicalisation, not a navigation.
  // A push would leave `/settings` in the history right before
  // `/settings/appearance`, and pressing Back would land on `/settings`
  // and immediately redirect forward again — the Back button would look
  // broken for anyone who opened Impostazioni from the sidebar.
  // Guarded on the location actually being under /settings: $location is a
  // global store, so without the prefix check this reactive block fires one
  // last time while the user is navigating AWAY (urlSlug turns null for
  // /companies too) and the replace() bounces them straight back — a routing
  // trap that made the whole sidebar appear dead once Settings had mounted.
  //
  // The Email tab waits for the capabilities answer: until it arrives the tab
  // is hidden, and a deep link to /settings/email must not be bounced away by
  // a question that is still in flight.
  $: awaitingCapabilities = urlSlug === 'email' && $capabilities === null;
  $: if ($location.startsWith('/settings') && visibleTabs.length && !awaitingCapabilities
      && (!urlSlug || !visibleTabs.some(tab => tab.slug === urlSlug))) {
    replace(`/settings/${visibleTabs[0].slug}`);
  }
</script>

<Layout>
  <div class="view-head">
    <div class="view-head-copy">
      <h1 class="section-title">{$t('settings.title')}</h1>
      <p class="view-subtitle">{$t('settings.description')}</p>
    </div>
  </div>

  {#if visibleTabs.length === 0}
    <div class="card p-8 text-center text-muted">{$t('settings.noTabsAvailable')}</div>
  {:else}
    <!-- Tab bar — mirrors the underline-tab pattern used in Invoices.svelte
         (Inviate / Ricevute) and Customers.svelte (Clienti / Fornitori) so
         the visual language stays consistent across the app.

         Marked up as navigation rather than as a `tablist`, unlike those two:
         these tabs are real links to real URLs (/settings/:tab), which is what
         makes them deep-linkable and reload-proof. `role="tab"` would promise
         a screen-reader user the arrow-key model of a tab widget, which links
         do not have. `aria-current` is the equivalent that fits a link — it is
         what tells a non-sighted user which section they are in, since the
         active tab is otherwise marked only by colour and an underline. -->
    <nav class="tab-bar mb-8" aria-label={$t('settings.tabsGroupLabel')}>
      {#each visibleTabs as tab (tab.slug)}
        {@const isActive = tab.slug === activeSlug}
        <a href="/settings/{tab.slug}" use:link aria-current={isActive ? 'page' : undefined}
          class="tab-button">
          <!-- svelte:component for the same legacy-mode reason as the panel
               below: visibleTabs is re-filtered when roles change, so a
               reused block must be able to swap its icon. -->
          <svelte:component this={tab.icon} size={14} aria-hidden="true" />
          <span>{$t(tab.labelKey)}</span>
        </a>
      {/each}
    </nav>

    {#if activeTab}
      <!-- Each panel sets `embedded={true}` to skip its own Layout wrapper
           and outermost title — Settings owns the chrome. The panel keeps
           its own internal subtitle/help text since that's feature-specific
           discovery copy (per CLAUDE.md UX guidance).

           `<svelte:component>` is required here, not a bare `<Tag />`: this
           file compiles in legacy (non-runes) mode, where a capitalised
           local variable used as a tag is resolved once when the block is
           created. Clicking a tab keeps the enclosing {#if} truthy, so the
           panel would never be re-created and the first tab would stay on
           screen for good. svelte:component re-instantiates on change. -->
      <svelte:component this={activeTab.component} embedded={true} />
    {/if}
  {/if}
</Layout>
